import toast from "react-hot-toast";

// API 베이스 URL 및 설정
export const API_BASE_URL =
  import.meta.env.VITE_API_URL || "http://localhost:8080/api";

// 개발 환경에서만 디버그 로그 출력
const debugLog = (...args: unknown[]) => {
  if (import.meta.env.MODE === "development") {
    console.debug(...args);
  }
};

// 토큰 갱신 중복 방지를 위한 Promise 저장
let refreshTokenPromise: Promise<string> | null = null;

// 인증 오류 메시지 매핑
const AUTH_ERROR_MESSAGES: Record<string, string> = {
  "Access token expired": "인증이 만료되었습니다. 다시 로그인해주세요.",
  "Session expired due to inactivity":
    "장시간 사용하지 않아 세션이 만료되었습니다. 다시 로그인해주세요.",
  "Logged in from another device":
    "다른 기기에서 로그인되어 현재 세션이 종료되었습니다. 다시 로그인해주세요.",
  "Session not found": "세션이 존재하지 않습니다. 다시 로그인해주세요.",
};

// 로그아웃 처리 함수
const handleLogout = () => {
  // 로컬 스토리지에서 인증 관련 데이터 제거
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("userInfo");

  // 로그인 페이지로 리다이렉트
  window.location.href = "/auth";
};

// 토큰 갱신 함수
const refreshAccessToken = async (): Promise<string> => {
  // 이미 갱신 중이면 기존 Promise 반환 (중복 방지)
  if (refreshTokenPromise) {
    return refreshTokenPromise;
  }

  refreshTokenPromise = (async () => {
    try {
      const refreshToken = localStorage.getItem("refreshToken");
      if (!refreshToken) {
        throw new Error("No refresh token available");
      }

      const response = await fetch(`${API_BASE_URL}/users/refresh`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        throw new Error("Token refresh failed");
      }

      const data = await response.json();
      const newAccessToken = data.data?.accessToken || data.accessToken;
      const newRefreshToken = data.data?.refreshToken || data.refreshToken;

      if (!newAccessToken) {
        throw new Error("No access token in refresh response");
      }

      // 새 토큰들 저장
      localStorage.setItem("accessToken", newAccessToken);
      if (newRefreshToken) {
        localStorage.setItem("refreshToken", newRefreshToken);
      }

      return newAccessToken;
    } catch (error) {
      // 갱신 실패 시 로그아웃
      handleLogout();
      throw error;
    } finally {
      // Promise 초기화
      refreshTokenPromise = null;
    }
  })();

  return refreshTokenPromise;
};

export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export interface ApiError {
  success: false;
  message: string;
  status: number;
}

class ApiClient {
  protected baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  protected async request<T>(
    endpoint: string,
    options: RequestInit = {},
    isRetry = false
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;

    // 자동으로 Authorization 헤더 추가
    const accessToken = localStorage.getItem("accessToken");
    const defaultHeaders: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (accessToken) {
      defaultHeaders.Authorization = `Bearer ${accessToken}`;
    }

    const config: RequestInit = {
      headers: {
        ...defaultHeaders,
        ...(options.headers as Record<string, string>),
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);

      // 응답 body가 있는지 확인 (Content-Length 헤더나 상태 코드로 판단)
      const contentLength = response.headers.get("content-length");
      const hasBody =
        contentLength !== "0" &&
        response.status !== 204 && // No Content
        response.status !== 304; // Not Modified

      let data = null;
      if (hasBody) {
        try {
          data = await response.json();
        } catch (jsonError) {
          // JSON 파싱 실패 시 텍스트로 시도
          debugLog("JSON parsing failed, trying text:", jsonError);
          try {
            data = await response.text();
          } catch (textError) {
            debugLog("Text parsing failed:", textError);
            data = null;
          }
        }
      }

      if (!response.ok) {
        // 401 Unauthorized 오류 처리
        if (response.status === 401 && !isRetry) {
          // 특정 인증 오류 메시지 처리 (로그아웃 필요)
          const errorMessage = data?.message;
          if (errorMessage && AUTH_ERROR_MESSAGES[errorMessage]) {
            const userMessage = AUTH_ERROR_MESSAGES[errorMessage];
            toast.error(userMessage);
            handleLogout();
            return Promise.reject({
              success: false,
              status: 401,
              message: userMessage,
            } as ApiError);
          }
          console.warn("401 Unauthorized - 토큰 갱신 시도");

          // refreshToken이 있으면 갱신 시도
          const refreshToken = localStorage.getItem("refreshToken");
          if (refreshToken) {
            try {
              // 토큰 갱신
              await refreshAccessToken();

              return this.request<T>(endpoint, options, true);
            } catch (refreshError) {
              console.error("토큰 갱신 실패:", refreshError);
              // 갱신 실패 시 로그아웃은 refreshAccessToken 내부에서 처리됨
              return Promise.reject({
                success: false,
                status: 401,
                message: "인증이 만료되었습니다. 다시 로그인해주세요.",
              } as ApiError);
            }
          } else {
            // refreshToken이 없으면 바로 로그아웃
            console.warn("refreshToken 없음 - 자동 로그아웃 처리");
            handleLogout();
            return Promise.reject({
              success: false,
              status: 401,
              message: "인증이 만료되었습니다. 다시 로그인해주세요.",
            } as ApiError);
          }
        }

        throw {
          success: false,
          status: response.status,
          message:
            data?.message || `HTTP ${response.status} 오류가 발생했습니다.`,
          ...(data && typeof data === "object" ? data : {}),
        } as ApiError;
      }

      return {
        success: true,
        data,
      };
    } catch (error) {
      // 401 오류는 이미 위에서 처리했으므로 그대로 전파
      if (
        error &&
        typeof error === "object" &&
        "status" in error &&
        error.status === 401
      ) {
        throw error;
      }

      if (error && typeof error === "object" && "success" in error) {
        throw error;
      }

      throw {
        success: false,
        message: "네트워크 오류가 발생했습니다.",
        status: 0,
      } as ApiError;
    }
  }

  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: "GET" });
  }

  async post<T>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async delete<T>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: "DELETE",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async patch<T>(endpoint: string, data?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: "PATCH",
      body: data ? JSON.stringify(data) : undefined,
    });
  }
}

export const apiClient = new ApiClient(API_BASE_URL);

// 관리자 전용 API 클라이언트
class AdminApiClient extends ApiClient {
  protected async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;

    // 관리자 토큰을 Authorization 헤더에 추가
    const adminAccessToken = localStorage.getItem("adminAccessToken");
    const defaultHeaders: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (adminAccessToken) {
      defaultHeaders.Authorization = `Bearer ${adminAccessToken}`;
    }

    const config: RequestInit = {
      headers: {
        ...defaultHeaders,
        ...(options.headers as Record<string, string>),
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);

      // 응답 body가 있는지 확인 (Content-Length 헤더나 상태 코드로 판단)
      const contentLength = response.headers.get("content-length");
      const hasBody =
        contentLength !== "0" &&
        response.status !== 204 && // No Content
        response.status !== 304; // Not Modified

      let data = null;
      if (hasBody) {
        try {
          data = await response.json();
        } catch (jsonError) {
          // JSON 파싱 실패 시 텍스트로 시도
          debugLog("JSON parsing failed, trying text:", jsonError);
          try {
            data = await response.text();
          } catch (textError) {
            debugLog("Text parsing failed:", textError);
            data = null;
          }
        }
      }

      if (!response.ok) {
        // 401 Unauthorized 오류 시 관리자 로그아웃
        if (response.status === 401) {
          console.warn("Admin 401 Unauthorized - 관리자 로그아웃 처리");
          localStorage.removeItem("adminAccessToken");
          localStorage.removeItem("adminUserInfo");
          window.location.href = "/admin/auth";
          return Promise.reject({
            success: false,
            status: 401,
            message: "관리자 인증이 만료되었습니다. 다시 로그인해주세요.",
          } as ApiError);
        }

        throw {
          success: false,
          status: response.status,
          message:
            data?.message || `HTTP ${response.status} 오류가 발생했습니다.`,
          ...(data && typeof data === "object" ? data : {}),
        } as ApiError;
      }

      return {
        success: true,
        data,
      };
    } catch (error) {
      // 401 오류는 이미 위에서 처리했으므로 그대로 전파
      if (
        error &&
        typeof error === "object" &&
        "status" in error &&
        error.status === 401
      ) {
        throw error;
      }

      if (error && typeof error === "object" && "success" in error) {
        throw error;
      }

      throw {
        success: false,
        message: "네트워크 오류가 발생했습니다.",
        status: 0,
      } as ApiError;
    }
  }
}

export const adminApiClient = new AdminApiClient(API_BASE_URL);

// 운영자 전용 API 클라이언트
class OperatorApiClient extends ApiClient {
  protected async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;

    // 운영자 토큰을 Authorization 헤더에 추가
    const operatorAccessToken = localStorage.getItem("operatorAccessToken");
    const defaultHeaders: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (operatorAccessToken) {
      defaultHeaders.Authorization = `Bearer ${operatorAccessToken}`;
    }

    const config: RequestInit = {
      headers: {
        ...defaultHeaders,
        ...(options.headers as Record<string, string>),
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);

      // 응답 body가 있는지 확인 (Content-Length 헤더나 상태 코드로 판단)
      const contentLength = response.headers.get("content-length");
      const hasBody =
        contentLength !== "0" &&
        response.status !== 204 && // No Content
        response.status !== 304; // Not Modified

      let data = null;
      if (hasBody) {
        try {
          data = await response.json();
        } catch (jsonError) {
          // JSON 파싱 실패 시 텍스트로 시도
          debugLog("JSON parsing failed, trying text:", jsonError);
          try {
            data = await response.text();
          } catch (textError) {
            debugLog("Text parsing failed:", textError);
            data = null;
          }
        }
      }

      if (!response.ok) {
        // 401 Unauthorized 오류 시 운영자 로그아웃
        if (response.status === 401) {
          console.warn("Operator 401 Unauthorized - 운영자 로그아웃 처리");
          localStorage.removeItem("operatorAccessToken");
          localStorage.removeItem("operatorUserInfo");
          window.location.href = "/operator/auth";
          return Promise.reject({
            success: false,
            status: 401,
            message: "운영자 인증이 만료되었습니다. 다시 로그인해주세요.",
          } as ApiError);
        }

        // 403 Forbidden 오류 처리
        if (response.status === 403) {
          console.warn("Operator 403 Forbidden - 권한 없음");
          return Promise.reject({
            success: false,
            status: 403,
            message: "접근 권한이 없습니다.",
          } as ApiError);
        }

        throw {
          success: false,
          status: response.status,
          message:
            data?.message || `HTTP ${response.status} 오류가 발생했습니다.`,
          ...(data && typeof data === "object" ? data : {}),
        } as ApiError;
      }

      return {
        success: true,
        data,
      };
    } catch (error) {
      // 401/403 오류는 이미 위에서 처리했으므로 그대로 전파
      if (
        error &&
        typeof error === "object" &&
        "status" in error &&
        (error.status === 401 || error.status === 403)
      ) {
        throw error;
      }

      if (error && typeof error === "object" && "success" in error) {
        throw error;
      }

      throw {
        success: false,
        message: "네트워크 오류가 발생했습니다.",
        status: 0,
      } as ApiError;
    }
  }
}

export const operatorApiClient = new OperatorApiClient(API_BASE_URL);
